import { useContext } from 'react';

import { css } from '@compiled/react';
import { FilterableCardItemDataContext } from 'src/uniform/contexts/filterable-card-item-data-context';

import { useLocale } from '@/libraries/web-framework/src/localization/use-locale';

import { useWAEContext } from '../../../utils/wae-context-provider';
import UniformCard, { type UniformCardProps } from '../../card/src/card';

export type FilterableCardProps = UniformCardProps;

const PATH_PREFIX = '$:';
const FALLBACK_PATH_SEPARATOR = '||';

const resolveValue = <T,>(raw: T | undefined, itemData: unknown): T | undefined => {
	if (typeof raw !== 'string') return raw;
	if (!raw.startsWith(PATH_PREFIX)) return raw;

	const fallbackPaths = raw
		.slice(PATH_PREFIX.length)
		.split(FALLBACK_PATH_SEPARATOR)
		.map((path) => path.trim())
		.filter(Boolean);

	for (const path of fallbackPaths) {
		const value = getByPath(itemData, path);
		if (isPresentValue(value)) return value as T;
	}

	return undefined;
};

const isPresentValue = (value: unknown): boolean =>
	value !== undefined && value !== null && value !== '';

const DEFAULT_LOCALE = 'en';

const isAbsoluteUrl = (value: string): boolean => /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(value);

const isAlreadyLocalized = (value: string, locale: string): boolean =>
	value === `/${locale}` || value.startsWith(`/${locale}/`);

const withLocalePrefix = (value: unknown, locale: string | undefined): unknown => {
	if (typeof value !== 'string' || value.length === 0) return value;
	if (!locale || locale === DEFAULT_LOCALE) return value;
	if (isAbsoluteUrl(value)) return value;
	if (isAlreadyLocalized(value, locale)) return value;
	const path = value.startsWith('/') ? value : `/${value}`;
	return `/${locale}${path}`;
};

const getByPath = (obj: unknown, path: string): unknown => {
	if (obj === null || obj === undefined) return undefined;
	return path
		.split('.')
		.reduce<unknown>(
			(acc, key) =>
				acc !== null && acc !== undefined && typeof acc === 'object'
					? (acc as Record<string, unknown>)[key]
					: undefined,
			obj,
		);
};

const FilterableCard = (rawProps: FilterableCardProps): JSX.Element => {
	const itemData = useContext(FilterableCardItemDataContext);
	const intlLocale = useLocale();
	const { locale: contextLocale } = useWAEContext();
	const activeLocale = contextLocale ?? intlLocale;

	const resolvedTitle = resolveValue(rawProps.title, itemData);
	const resolvedDataLabelEnglish =
		resolveValue(rawProps.dataLabelEnglish, itemData) ?? resolvedTitle;

	const resolved: UniformCardProps = {
		alignment: resolveValue(rawProps.alignment, itemData),
		orientation: resolveValue(rawProps.orientation, itemData),
		columns: resolveValue(rawProps.columns, itemData),
		background: resolveValue(rawProps.background, itemData),
		eyebrow: resolveValue(rawProps.eyebrow, itemData),
		logo: resolveValue(rawProps.logo, itemData),
		title: resolvedTitle,
		titleAs: resolveValue(rawProps.titleAs, itemData),
		// Per-template attribution for the gallery card CTA click event.
		// An explicit dataLabel (incl. a "$:" binding) wins; otherwise fall
		// back to the resolved template title.
		dataLabel: resolveValue(rawProps.dataLabel, itemData) ?? resolvedTitle,
		dataLabelEnglish: resolvedDataLabelEnglish,
		body: resolveValue(rawProps.body, itemData),
		richText: resolveValue(rawProps.richText, itemData),
		linkText: resolveValue(rawProps.linkText, itemData),
		linkUrl: withLocalePrefix(resolveValue(rawProps.linkUrl, itemData), activeLocale) as
			| string
			| undefined,
		imageUrl: resolveValue(rawProps.imageUrl, itemData),
		imageAlt: resolveValue(rawProps.imageAlt, itemData),
		iconUrl: resolveValue(rawProps.iconUrl, itemData),
		iconSize: resolveValue(rawProps.iconSize, itemData),
		iconPaddingIncluded: resolveValue(rawProps.iconPaddingIncluded, itemData),
		spotIllustrationUrl: resolveValue(rawProps.spotIllustrationUrl, itemData),
		spotIllustrationAlt: resolveValue(rawProps.spotIllustrationAlt, itemData),
		spotIllustrationWidth: resolveValue(rawProps.spotIllustrationWidth, itemData),
	};

	return (
		<div css={croppedImageWrapper}>
			<UniformCard {...resolved} />
		</div>
	);
};

// Filterable gallery cards sit in an equal-width, equal-height grid, so the
// card boxes already match. The shared Card renders its image with
// `object-fit: contain`, which letterboxes any source image that is not exactly
// 16:9 — making the *visible* image shorter on some cards than others within the
// same row. Forcing `object-fit: cover` (and filling the 16:9 box height) crops
// the overflow instead, so every card's image renders at the same height.
//
// This override lives here (not in the shared Card) so only the filterable
// gallery is affected. The `css` constant is intentionally local to this file:
// imported Compiled style objects can render without their generated classes in
// the filterable gallery context.
const croppedImageWrapper = css({
	// The gallery stretches each card to fill an equal-height grid row via the
	// chain: <li height:100%> → <div fullHeightCardStyle height:100%> → CARD.
	// This wrapper sits in that chain, so it MUST forward the height (height:100%
	// + display:grid) — otherwise it collapses to its content and the card no
	// longer fills the row, breaking card-height parity across the row.
	height: '100%',
	display: 'grid',
	// Issue in Picture component applies styles to picture instead of img
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.cardImageContainer img': {
		// Lock the image box to a true 16:9 derived from its width. Without
		// pinning the aspect ratio here, the rendered `aspect-ratio` resolves to
		// `auto` and the box height is driven by each source image's intrinsic
		// ratio — so cards in the same row get different image heights (e.g.
		// 208px for a 16:9 source vs 236px for a 3:2 source). Pinning width +
		// aspectRatio makes every box the same height; `cover` then crops the
		// overflow instead of letterboxing.
		width: '100%',
		height: 'auto',
		aspectRatio: '16 / 9',
		objectFit: 'cover',
		// Anchor the crop to the top of the source image. Many template
		// thumbnails are taller than 16:9 (e.g. portrait document
		// screenshots), so the default `object-position: center` crops their
		// header/title off the top and bottom equally and shows the middle of
		// the document. Top-anchoring keeps the meaningful top of each
		// template visible while still cropping the overflow at the bottom.
		objectPosition: 'top center',
	},
});

export default FilterableCard;
